import { getGeoEntitiesFromSlugs } from '@vakantiesnl/components/src/utils/geo';
import { type GeoState } from '@vakantiesnl/services/src/hooks/queries';
import { themes, attributes, airlines } from '@vakantiesnl/services/src/route-params';
import {
	FILTER_KEYS_TO_COUNT,
	type FilterState,
	getSelectedChainsBySlugs,
	getSelectedTourOperatorsBySlugs,
} from '@vakantiesnl/services/src/stores/filtersStore';
import type { GlobalFiltersState } from '@vakantiesnl/services/src/stores/globalFiltersStore/interfaces';
import { getFormattedRoundedPrice } from '@vakantiesnl/services/src/util/mathUtils';
import { type Chain, type MicroCopy, type TourOperator } from '@vakantiesnl/types';
import { type MappedDestinationView } from '@vakantiesnl/types/src/geo';

export interface SingleFilter {
	filterType: string;
	filterLabel: string;
	value: string;
}

export const mapFilterLabels = (
	filters: FilterState,
	globalFilters: GlobalFiltersState,
	microCopies: MicroCopy,
	geo: GeoState,
): SingleFilter[] => {
	const { country, region, city } = getGeoEntitiesFromSlugs(filters, geo);
	const chains = getSelectedChainsBySlugs(filters.chainsSlugs);
	const tourOperators = getSelectedTourOperatorsBySlugs(filters.tourOperatorsSlugs);
	const activeFilters: SingleFilter[] = [];

	const combinedFilters = {
		...filters,
		...globalFilters,
	};

	const isOwnTransport = filters.transport === 'OWN';

	FILTER_KEYS_TO_COUNT.forEach((key) => {
		if (Array.isArray(combinedFilters[key])) {
			const filterArray = combinedFilters[key] as string[];

			switch (key) {
				case 'countrySlugs':
					country.forEach((item: MappedDestinationView) => {
						activeFilters.push({
							filterType: key,
							filterLabel: item.name,
							value: item.slug,
						});
					});
					break;
				case 'regionSlugs':
					region.forEach((item: MappedDestinationView) => {
						activeFilters.push({
							filterType: key,
							filterLabel: item.name,
							value: item.slug,
						});
					});
					break;
				case 'citySlugs':
					city.forEach((item: MappedDestinationView) => {
						activeFilters.push({
							filterType: key,
							filterLabel: item.name,
							value: item.slug,
						});
					});
					break;
				case 'durations':
					filterArray.forEach((item: string) => {
						activeFilters.push({
							filterType: key,
							filterLabel: `${item} ${microCopies['common.days']}`,
							value: item,
						});
					});
					break;
				case 'departureAirport':
					if (filters.transport !== 'OWN') {
						filterArray.forEach((item: string) => {
							activeFilters.push({
								filterType: key,
								filterLabel: item,
								value: item,
							});
						});
					}
					break;
				case 'facilities':
				case 'distances':
				case 'awards':
					filterArray.forEach((item: string) => {
						activeFilters.push({
							filterType: key,
							filterLabel: microCopies[`accommodationFilter.label.${item}`] || item,
							value: item,
						});
					});
					break;
				case 'mealplans':
					if (!isOwnTransport) {
						filterArray.forEach((item: string) => {
							activeFilters.push({
								filterType: key,
								filterLabel: microCopies[`filters.${key}.labels.${item}`] || item,
								value: item,
							});
						});
					}
					break;
				case 'themes':
					filterArray.forEach((item: string) => {
						activeFilters.push({
							filterType: key,
							filterLabel: microCopies[`filters.${key}.labels.${item}`] || themes[item] || item,
							value: item,
						});
					});
					break;
				case 'attributes':
					filterArray.forEach((item: string) => {
						activeFilters.push({
							filterType: key,
							filterLabel: attributes[item] || item,
							value: item,
						});
					});
					break;
				case 'airlines':
					filterArray.forEach((item: string) => {
						activeFilters.push({
							filterType: key,
							filterLabel: airlines[item] || item,
							value: item,
						});
					});
					break;
				case 'chainsSlugs':
					chains.forEach((chain: Chain) => {
						activeFilters.push({
							filterType: key,
							filterLabel: chain.name,
							value: chain.slug,
						});
					});
					break;
				case 'tourOperatorsSlugs':
					if (filters.transport !== 'OWN') {
						tourOperators.forEach((tourOperator: TourOperator) => {
							activeFilters.push({
								filterType: key,
								filterLabel: tourOperator.name,
								value: tourOperator.slug,
							});
						});
					}
					break;
				default:
					filterArray.forEach((item: string) => {
						activeFilters.push({
							filterType: key,
							filterLabel: microCopies[`filters.${key}.labels.${item}`] || item,
							value: item,
						});
					});
					break;
			}
		} else {
			if (filters.transport && key === 'transport') {
				if (isOwnTransport) {
					activeFilters.push({
						filterType: 'transport',
						filterLabel: microCopies[`filters.transport.labels.${filters.transport}`],
						value: filters.transport,
					});
				}
			}

			if (filters.rating && key === 'rating') {
				activeFilters.push({
					filterType: 'rating',
					filterLabel: microCopies[`filters.rating.labels.${filters.rating}`],
					value: filters.rating,
				});
			}

			if (filters.flightProvider && key === 'flightProvider') {
				activeFilters.push({
					filterType: 'flightProvider',
					filterLabel: filters.flightProvider,
					value: filters.flightProvider,
				});
			}

			if (!isOwnTransport) {
				if (filters.priceMin && key === 'priceMin') {
					activeFilters.push({
						filterType: 'priceMin',
						filterLabel: `${microCopies['filters.priceRange.min']}: ${getFormattedRoundedPrice(
							Number(filters.priceMin) * 100,
						)}`,
						value: filters.priceMin,
					});
				}
				if (filters.priceMax && key === 'priceMax') {
					activeFilters.push({
						filterType: 'priceMax',
						filterLabel: `${microCopies['filters.priceRange.max']}: ${getFormattedRoundedPrice(
							Number(filters.priceMax) * 100,
						)}`,
						value: filters.priceMax,
					});
				}
			}
		}
	});

	return activeFilters;
};
